<template>
  <div class="navbar" :class="{ scrolled: isScrolled }">
    <div class="navbar-top">
      <div class="page-container">
        <div class="left-section">
          <Logo to="/" />
        </div>
      </div>
    </div>

    <div class="navbar-main">
      <div class="page-container main-container">
        <el-menu
          :default-active="activeIndex"
          class="main-menu"
          mode="horizontal"
          background-color="#A65226"
          text-color="#fff"
          active-text-color="#fff"
          @select="handleSelect"
          :ellipsis="false"
        >
          <!-- 固定的首页菜单 -->
          <el-menu-item index="/"> 首页 </el-menu-item>

          <!-- 动态渲染菜单 -->
          <template v-for="menu in filteredMenuList" :key="menu.menuId">
            <!-- 有子菜单的情况 -->
            <el-sub-menu
              v-if="menu.children && menu.children.length > 0"
              :index="menu.path || `/menu-${menu.menuId}`"
              @click="handleParentMenuClick(menu)"
            >
              <template #title>{{ menu.menuName }}</template>
              <el-menu-item
                v-for="child in menu.children"
                :key="child.menuId"
                :index="child.path"
                @click="handleSelect(child.path, child)"
              >
                <span style="font-size: 20px">{{ child.menuName }}</span>
              </el-menu-item>
            </el-sub-menu>

            <!-- 没有子菜单的情况 -->
            <el-menu-item
              v-else
              :index="menu.path"
              @click="handleSelect(menu.path, menu)"
            >
              {{ menu.menuName }}
            </el-menu-item>
          </template>
        </el-menu>
      </div>
    </div>

    <!-- 移动端菜单 -->
    <div class="mobile-navbar">
      <div class="mobile-navbar-top">
        <div class="mobile-logo">
          <Logo to="/" />
        </div>
        <div class="mobile-actions">
          <el-icon class="icon-btn" @click="showSearch = !showSearch"
            ><Search
          /></el-icon>
          <el-icon class="icon-btn" @click="toggleMobileMenu">
            <Menu />
            <!-- <component :is="mobileMenuOpen ? Close : Menu" /> -->
          </el-icon>
        </div>
      </div>

      <div class="mobile-search" v-if="showSearch">
        <el-input
          placeholder="搜索"
          v-model="searchKeyword"
          @keyup.enter="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
      </div>

      <div class="mobile-menu" v-if="mobileMenuOpen">
        <el-collapse accordion>
          <!-- 固定的首页菜单 -->
          <el-collapse-item title="首页" name="home">
            <router-link to="/" @click="toggleMobileMenu"> 首页 </router-link>
          </el-collapse-item>

          <template v-for="menu in filteredMenuList" :key="menu.menuId">
            <!-- 有子菜单的情况 -->
            <el-collapse-item
              v-if="menu.children && menu.children.length > 0"
              :title="menu.menuName"
              :name="`menu-${menu.menuId}`"
            >
              <router-link
                v-for="child in menu.children"
                :key="child.menuId"
                :to="child.path || `/menu-${child.menuId}`"
                @click="toggleMobileMenu"
              >
                {{ child.menuName }}
              </router-link>
            </el-collapse-item>

            <!-- 没有子菜单的情况 -->
            <el-collapse-item
              v-else
              :title="menu.menuName"
              :name="`menu-${menu.menuId}`"
            >
              <router-link
                :to="menu.path || `/menu-${menu.menuId}`"
                @click="toggleMobileMenu"
              >
                {{ menu.menuName }}
              </router-link>
            </el-collapse-item>
          </template>
        </el-collapse>
      </div>
    </div>
  </div>
</template>

<script>
import { useRoute, useRouter } from "vue-router";
import { ref, computed, onMounted, onUnmounted, watch, provide } from "vue";
import { Search, Menu, Close } from "@element-plus/icons-vue";
import Logo from "./Logo.vue";
import { menuList } from "@/api/menu";

export default {
  name: "Navbar",
  components: {
    Logo,
    Search,
    Menu,
    Close,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const isScrolled = ref(false);
    const mobileMenuOpen = ref(false);
    const showSearch = ref(false);
    const searchKeyword = ref("");
    const menuTabList = ref([]);
    const oldPath = ref("");
    const newPath = ref("");

    // 计算过滤后的菜单列表（去掉名为"22"的菜单）
    const filteredMenuList = computed(() => {
      return menuTabList.value.filter((menu) => {
        // 过滤掉菜单名称为"22"的菜单项
        if (menu.menuName === "22") return false;

        // 如果有子菜单，也过滤掉子菜单中名称为"22"的项
        if (menu.children && menu.children.length > 0) {
          menu.children = menu.children.filter(
            (child) => child.menuName !== "22"
          );
        }

        return true;
      });
    });

    // 计算当前活动菜单
    const activeIndex = computed(() => {
      const cur = route.path;
      const old = newPath.value;
      if (old !== "/" && old) {
        localStorage.setItem("oldRoute", old);
      }

      if (cur.includes("/expertIntroduction/detail")) {
        const lao = localStorage.getItem("oldRoute");
        // 如果想在“从 specialist 进入详情”时额外清空 detailId，就在这里处理
        if (old === "/expertIntroduction/specialist") {
          localStorage.setItem("detailId", "空");
        }
        return lao;
      }

      // 非详情页正常返回当前路径
      return cur;
    });

    // 处理滚动事件
    const handleScroll = () => {
      if (typeof window !== "undefined") {
        isScrolled.value = window.scrollY > 50;
      }
    };

    // 切换移动端菜单
    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value;
      if (mobileMenuOpen.value) {
        showSearch.value = false;
      }
    };
    // 处理父级菜单点击（当有子菜单时）
    const handleParentMenuClick = (menu) => {
      // 如果没有路径则跳转到第一个子菜单
      const firstChildPath = menu.children[0].path;
      if (firstChildPath) {
        handleSelect(firstChildPath, menu.children[0]);
      }
    };

    // 搜索处理
    const handleSearch = () => {
      if (searchKeyword.value && searchKeyword.value.trim()) {
        router.push({
          path: "/search",
          query: { keyword: searchKeyword.value },
        });
        searchKeyword.value = "";
        showSearch.value = false;
      }
    };
    // 提供响应式的当前菜单
    const currentMenu = ref();
    provide("currentMenu", currentMenu);
    // 处理菜单选择
    const handleSelect = (path, menuItem) => {
      if (path) {
        // 更新当前菜单
        const findMenuData = (menus, path) => {
          for (const menu of menus) {
            if (menu.path === path) return menu;
            if (menu.children) {
              const found = findMenuData(menu.children, path);
              if (found) return found;
            }
          }
          return;
        };

        currentMenu.value = findMenuData(menuTabList.value, path);

        if (currentMenu.value) {
          localStorage.setItem(
            "currentMenu",
            JSON.stringify(currentMenu.value)
          );
          localStorage.setItem("detailId", "空");
        }

        router.push(path);
      }
    };

    // 登录处理
    const handleLoginClick = () => {
      if (typeof window !== "undefined") {
        window.location.href = "http://10.0.0.17:8080";
      }
    };

    // 获取菜单数据
    const fetchMenuList = async () => {
      try {
        const res = await menuList();
        if (res.data) {
          // 过滤掉不需要显示的菜单项（如果需要）
          menuTabList.value = res.data.data || res.data;
          // console.log("菜单数据加载成功:", menuTabList.value);
          // localStorage.getItem('menuTabList', menuTabList)
          localStorage.setItem(
            "menuTabList",
            JSON.stringify(menuTabList.value)
          );
        }
      } catch (error) {
        console.error("获取菜单失败:", error);
        // 可以设置默认菜单或显示错误
      }
    };

    // 路由变化时关闭移动菜单
    watch(
      () => route.path,
      (oldPaths, newPaths) => {
        console.log("真的是在这里", oldPaths, newPaths);
        oldPath.value = oldPaths;
        newPath.value = newPaths;
        mobileMenuOpen.value = false;
        showSearch.value = false;
      }
    );

    onMounted(() => {
      fetchMenuList();
      if (typeof window !== "undefined") {
        window.addEventListener("scroll", handleScroll);
        handleScroll();
      }
    });

    return {
      activeIndex,
      isScrolled,
      mobileMenuOpen,
      showSearch,
      searchKeyword,
      menuTabList,
      filteredMenuList, // 返回过滤后的菜单列表
      toggleMobileMenu,
      handleSearch,
      handleSelect,
      handleLoginClick,
      handleParentMenuClick,
    };
  },
};
</script>

<style lang="scss" scoped>
/* 响应式设计改进 */
.navbar {
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  transition: all 0.3s ease;

  .navbar-top {
    height: 5rem; /* 使用相对单位 */
    background-color: #fff;

    .page-container {
      height: 100%;
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: url("./../assets/images/pageImage/header.png");
      background-size: 100% 100%;
      min-width: 100% !important;
      padding: 0 10%; /* 使用百分比代替固定像素 */

      @media (max-width: 1600px) {
        padding: 0 8%;
      }

      @media (max-width: 1200px) {
        padding: 0 5%;
      }
    }

    .left-section {
      display: flex;
      align-items: center;
    }
  }

  .navbar-main {
    background: url("./../assets/images/pageImage/navBg.png");
    background-size: 100% 100%;

    .main-container {
      display: flex;
      justify-content: center; /* 居中显示菜单 */
      width: 100%;
      overflow: hidden; /* 防止溢出 */
    }

    .main-menu {
      border-bottom: none;
      display: flex;
      justify-content: center;
      width: auto; /* 改为自动宽度 */
      flex-wrap: nowrap; /* 禁止换行 */
      white-space: nowrap; /* 禁止文本换行 */
      overflow-x: auto; /* 允许水平滚动 */
      -webkit-overflow-scrolling: touch; /* 平滑滚动 */

      /* 隐藏滚动条但保持滚动功能 */
      &::-webkit-scrollbar {
        display: none;
      }
      -ms-overflow-style: none;
      scrollbar-width: none;

      :deep(.el-menu--horizontal) {
        display: flex;
        flex-wrap: nowrap;
        white-space: nowrap;
        border: none;
      }

      :deep(.el-menu-item),
      :deep(.el-sub-menu__title) {
        height: 3.75rem;
        line-height: 3.75rem;
        font-size: 1.3rem; /* 使用固定字体大小确保一致性 */
        font-weight: 500;
        padding: 0 1.2rem; /* 使用相对较小的内边距 */
        min-width: auto;
        width: auto;
        text-align: center;
        position: relative;
        z-index: 1;
        border-bottom: none !important;
        box-sizing: border-box;
        flex: 0 0 auto; /* 不伸缩不收缩 */
        white-space: nowrap;

        &.is-active,
        .el-sub-menu.is-active & {
          color: #a65226 !important;
          border-bottom: none;
          background-color: #a65226 !important;

          &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: url("./../assets/images/pageImage/navActive.png")
              no-repeat center center;
            background-size: 90% 80%;
            z-index: -1;
            border-bottom: none;
          }
        }

        &:hover {
          background-color: #a65226 !important;
        }
      }

      :deep(.el-sub-menu) {
        height: 3.75rem;
        line-height: 3.75rem;
        font-size: 1.3rem;
        font-weight: 500;
        padding: 0;
        min-width: auto;
        width: auto;
        text-align: center;
        margin: 0;
        box-sizing: border-box;
        flex: 0 0 auto;
        white-space: nowrap;

        &.is-active {
          .el-sub-menu__title {
            display: flex;
            justify-content: center;
            align-items: center;
            background: url("./../assets/images/pageImage/navActive.png")
              no-repeat center center;
            background-size: 90% 80%;
            position: relative;
            z-index: 1;
            color: #a65226 !important;
            border-bottom: none !important;
            padding: 0 1.2rem;

            &::before {
              content: "";
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: url("./../assets/images/pageImage/navActive.png")
                no-repeat center center;
              background-size: 90% 80%;
              z-index: -1;
              border-bottom: none;
            }
          }
        }

        &:hover {
          .el-sub-menu__title {
            border-bottom: none !important;
            background-color: #a65226 !important;
          }
        }
      }

      :deep(.el-sub-menu__icon-arrow) {
        display: none !important;
      }

      /* 中等屏幕调整 - 减小内边距 */
      @media (max-width: 1600px) {
        :deep(.el-menu-item),
        :deep(.el-sub-menu__title) {
          padding: 0 1rem;
          font-size: 1.2rem;
        }

        :deep(.el-sub-menu.is-active .el-sub-menu__title) {
          padding: 0 1rem;
        }
      }

      /* 较小屏幕调整 - 进一步减小内边距和字体 */
      @media (max-width: 1400px) {
        :deep(.el-menu-item),
        :deep(.el-sub-menu__title) {
          padding: 0 1.2rem;
          font-size: 1.3rem;
          font-weight: 500;
        }

        :deep(.el-sub-menu.is-active .el-sub-menu__title) {
          padding: 0 1.2rem;
        }
      }

      /* 更小屏幕调整 - 最小化内边距 */
      @media (max-width: 1200px) {
        :deep(.el-menu-item),
        :deep(.el-sub-menu__title) {
          padding: 0 0.6rem;
          font-size: 1rem;
        }

        :deep(.el-sub-menu.is-active .el-sub-menu__title) {
          padding: 0 0.6rem;
        }
      }

      /* 超小屏幕调整 - 启用水平滚动 */
      @media (max-width: 1100px) {
        justify-content: flex-start; /* 左对齐以便滚动 */

        :deep(.el-menu-item),
        :deep(.el-sub-menu__title) {
          padding: 0 0.5rem;
          font-size: 0.95rem;
        }

        :deep(.el-sub-menu.is-active .el-sub-menu__title) {
          padding: 0 0.5rem;
        }
      }
    }
  }

  .mobile-navbar {
    display: none;
    background-color: #fff;

    .mobile-navbar-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 15px 20px;
    }

    .mobile-actions {
      display: flex;
      align-items: center;
      gap: 20px;

      .icon-btn {
        font-size: 24px;
        cursor: pointer;
        color: #333;
      }
    }

    .mobile-search {
      padding: 0 15px 15px;
    }

    .mobile-menu {
      color: #333;
    }

    :deep(.el-collapse-item__header) {
      padding: 0 15px;
      height: 50px;
      line-height: 50px;
      font-size: 16px;
      font-weight: 500;
      color: #333;
    }

    :deep(.el-collapse-item__content) {
      padding: 0;

      a {
        display: block;
        padding: 12px 30px;
        color: #666;
        text-decoration: none;
        border-bottom: 1px solid #eee;

        &:hover {
          color: var(--primary-color);
          background-color: #f0f0f0;
        }
      }
    }
  }
}

/* 响应式断点调整 - 提高阈值 */
@media (max-width: 992px) {
  .navbar {
    .navbar-top,
    .navbar-main {
      display: none;
    }

    .mobile-navbar {
      display: block;
    }
  }
}
</style>
